import { useState, useEffect, useCallback } from 'react';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  PrefOverlay, GhostCard, ShortlistToggleButton, ShortlistChipStrip,
  ShortlistSectionHeader, ShortlistEmptyState,
  ShortlistHintStrip, AddPhotoDialog, ItemDetailModal, PageHeader,
  useRankSync, moveInList, MATERIAL_TYPES, SAMPLE_QUOTAS,
} from './ShortlistShared3';
import {
  SourcePhotoLink, SourcePhotoPeek, RemovalNotice, useSourcePeek, useGlow,
} from './SourcePhoto';
import { RoomTabStrip, useRoomRemoval } from './RoomTabs';
import { formatMaterialPrice } from '../../../shared/formatPrice';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';
import { getTokenSubject } from '../../../auth/token';
import { LoadingState, ErrorState, useToast } from '../../../shared/components/status';

import {
  GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, WARNING,
  labelMono, bodyFont, appGridBg, PAGE_PX,
} from '../../../theme/tokens2';

// ─── Constants ────────────────────────────────────────────────────────────────

// The material types and sample box limits now live in ShortlistShared3.jsx,
// shared with Sample Delivery.
const QUOTAS = SAMPLE_QUOTAS;

const FIELDS = [
  { label: 'NAME',       field: 'namelabel',  placeholder: 'Material name',    type: 'text'   },
  { label: 'VENDOR',     field: 'vendor',     placeholder: 'Supplier / brand', type: 'text'   },
  { label: 'PRICE',      field: 'price',      placeholder: 'e.g. 4.60 / sqft', type: 'text', prefix: 'SGD', maxLength: 60 },
  { label: 'DIMENSIONS', field: 'dimensions', placeholder: '600 × 600 mm',     type: 'text'   },
  { label: 'LINK',       field: 'link',       placeholder: 'https://…',        type: 'url'    },
  { label: 'Notes',      field: 'notes',      placeholder: 'Notes…',           textarea: true },
];

// ─── MaterialCard ─────────────────────────────────────────────────────────────
// The samples toggle at the card's foot is the shared ShortlistToggleButton
// (variant 'tint' — this repeats across every card in a dense grid, so a
// full-solid button on each tile would compete with itself).
function MaterialCard({
  item, idx, arrLen,
  onMoveUp, onMoveDown, onRemove, onUpdated,
  onToggleSamples, scenesData, onOpenSource, isGlowing,
}) {
  const [modalOpen,   setModalOpen]   = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [cardRef,     glowSx]         = useGlow(isGlowing);

  const rank = String(idx + 1).padStart(2, '0');
  const name = item.namelabel || 'Untitled';

  return (
    <>
      <Box
        ref={cardRef}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
        sx={{
          border: item.savvyproposal ? `1.5px solid ${GREEN}` : `0.5px solid ${BORDER}`,
          borderRadius: '3px',
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex', flexDirection: 'column',
          scrollMarginTop: '12px', scrollMarginBottom: '12px',
          ...glowSx,
        }}
      >
        {/* ── Image area ── */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '3/4', overflow: 'hidden' }}>
          <Box
            component="button"
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label={`Open details for ${name}`}
            sx={{ display: 'block', width: '100%', height: '100%', p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={item.imagesrc}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>

          <Box sx={{
            position: 'absolute', bottom: 6, left: 6,
            ...labelMono, fontSize: '8px',
            background: 'rgba(18,18,18,0.62)', color: 'rgba(255,255,255,0.85)',
            px: '5px', py: '2px', pointerEvents: 'none',
          }}>
            {rank}
          </Box>

          {item.savvyproposal && (
            <Box sx={{
              position: 'absolute', bottom: 6, right: 6,
              ...labelMono, fontSize: '7px',
              background: 'rgba(255,255,255,0.92)', color: GREEN,
              border: `1px solid ${GREEN}`, px: '4px', py: '2px', pointerEvents: 'none',
            }}>
              Savvy pick
            </Box>
          )}

          <PrefOverlay
            onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove}
            isFirst={idx === 0} isLast={idx === arrLen - 1} cardHovered={cardHovered}
            itemLabel={name}
          />
        </Box>

        {/* ── Details ── */}
        <Box sx={{ px: '8px', pt: '7px', pb: '6px', borderTop: `0.5px solid ${BORDER}` }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}>
            <Typography sx={{
              fontSize: '11px', fontWeight: 500, color: CHARCOAL,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1, ...bodyFont,
            }}>
              {name}
            </Typography>
            <Box
              component="button" type="button" aria-label={`Edit details for ${name}`}
              onClick={() => setModalOpen(true)}
              sx={{
                background: 'none', border: 'none', cursor: 'pointer',
                p: 0, flexShrink: 0, color: MUTED_FAINT, display: 'flex',
                '&:hover': { color: GREEN }, transition: 'color 0.15s',
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: '0.75rem' }} />
            </Box>
          </Box>

          {/* Price first, then vendor. The price keeps its room; a long vendor
              name is cut with "…". A very long price is cut too, and shows in
              full when you point at it. */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mt: '2px', minWidth: 0, overflow: 'hidden' }}>
            <Typography
              title={formatMaterialPrice(item.price)}
              sx={{
                fontSize: '10px', fontWeight: 500, color: CHARCOAL, ...bodyFont,
                flexShrink: 0, maxWidth: '100%',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {formatMaterialPrice(item.price)}
            </Typography>
            {item.vendor && (
              <>
                <Typography sx={{ fontSize: '9px', color: MUTED_FAINT, flexShrink: 0 }}>·</Typography>
                <Typography sx={{
                  fontSize: '10px', color: MUTED, fontStyle: 'italic', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...bodyFont,
                }}>
                  {item.vendor}
                </Typography>
              </>
            )}
          </Box>

          {item.dimensions && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '2px' }}>
              {item.dimensions}
            </Typography>
          )}

          {item.link ? (
            <Box
              component="a" href={item.link} target="_blank" rel="noopener noreferrer"
              sx={{
                display: 'block', mt: '3px',
                ...labelMono, fontSize: '8px', color: GREEN,
                textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
              }}
            >
              Visit link ↗
            </Box>
          ) : (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '3px' }}>
              No link
            </Typography>
          )}

          {/* Opens the Moodboard photo this came from, on top of this page */}
          <SourcePhotoLink
            item={item} scenesData={scenesData} showRoom={false}
            onOpen={(it, scene) => onOpenSource(it, scene, `${it.cat_materials} #${rank}`)}
          />
        </Box>

        {/* ── Samples toggle row ──
            mt: 'auto' pins this to the bottom of the card, so the buttons line
            up across a row even when cards have different amounts of text.
            Samples can only be requested from vendors we can get physical
            samples from (see MaterialsShortlistController). */}
        {item.sampleeligible ? (
          <ShortlistToggleButton
            variant="tint"
            isMarked={item.in_samples_request}
            onToggle={(next) => onToggleSamples(item.id, next)}
            unmarkedLabel="+ Add to samples"
            markedLabel="✓ In samples request"
            removeLabel="Remove from samples"
            busyMarkLabel="Adding…"
            busyRemoveLabel="Removing…"
            sx={{ mt: 'auto' }}
          />
        ) : (
          <Box sx={{
            mt: 'auto', px: '8px', py: '7px',
            borderTop: `0.5px solid ${BORDER}`,
            ...labelMono, fontSize: '8px', color: MUTED_FAINT,
            textAlign: 'center', lineHeight: 1.4,
          }}>
            Samples not available yet — We are expanding our range
          </Box>
        )}
      </Box>

      <ItemDetailModal
        item={item} open={modalOpen}
        onClose={() => setModalOpen(false)} onUpdated={onUpdated}
        apiEndpoint={(id) => `/api/MaterialsShortlist/${id}`}
        fields={FIELDS}
      />
    </>
  );
}

// ─── RoomTabContent ───────────────────────────────────────────────────────────
// Shows all materials for the active room, filtered by the active material-type chip.
function RoomTabContent({
  activeRoom, items, phoneNumber, scenesData,
  onItemsChanged, onRemoveItem, onToggleSamples, onRefresh, onOpenSource, glowItemId,
}) {
  const toast = useToast();
  const [activeMaterialType, setActiveMaterialType] = useState('all');
  const [addPhotoFor,        setAddPhotoFor]        = useState(null); // material type id or null

  // Saves the new order only after the customer moves a card.
  const queueRankSave = useRankSync('/api/MaterialsShortlist/bulk');

  const updateTypeItems = (materialType, updatedList) => {
    const rest = items.filter((i) => i.cat_materials !== materialType);
    onItemsChanged([...rest, ...updatedList]);
  };

  const move = (materialType, list, idx, direction) => {
    const next = moveInList(list, idx, direction);
    if (!next) return;
    updateTypeItems(materialType, next);
    queueRankSave(next);
  };

  const removeItem = async (materialType, list, item) => {
    try {
      await apiFetch(`/api/MaterialsShortlist/${item.id}`, { method: 'DELETE' });
      const next = list
        .filter((i) => i.id !== item.id)
        .map((it, i) => ({ ...it, ranking: i + 1 }));
      updateTypeItems(materialType, next);
      onRemoveItem(item.id);
      if (next.length) queueRankSave(next);
      toast.success(`${item.namelabel || 'Item'} removed`);
    } catch (err) {
      toast.error("We couldn't remove this item. Please try again.");
    }
  };

  const handleItemUpdated = (updated) => {
    onItemsChanged(items.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  };

  const handlePhotoUploaded = async (url) => {
    if (!url) return; // the person removed the file inside the dialog
    try {
      await apiFetch('/api/MaterialsShortlist', {
        method: 'POST',
        body: { cat_room: activeRoom, cat_materials: addPhotoFor, imagesrc: url },
      });
      setAddPhotoFor(null);
      onRefresh(); // reload so the new card arrives with its server-given rank
    } catch (err) {
      toast.error("Your photo uploaded, but we couldn't add it to your shortlist. Please try again.");
    }
  };

  const visibleTypes = activeMaterialType === 'all'
    ? MATERIAL_TYPES.filter((mt) => items.some((i) => i.cat_materials === mt.id))
    : MATERIAL_TYPES.filter((mt) => mt.id === activeMaterialType);

  // Per-type item lists, for the chip counts
  const materialTypeGroups = MATERIAL_TYPES.reduce((acc, mt) => {
    acc[mt.id] = items.filter((i) => i.cat_materials === mt.id);
    return acc;
  }, {});

  return (
    <>
      {/* Material-type chips. No "+ Add" here: the types are a fixed list. */}
      <ShortlistChipStrip
        allCategories={MATERIAL_TYPES.map((mt) => mt.id)}
        setAllCategories={() => {}}
        activeChip={activeMaterialType}
        setActiveChip={setActiveMaterialType}
        grouped={materialTypeGroups}
        totalCount={items.length}
        allowAdd={false}
      />

      {/* ── Card grid(s) — one section per visible material type ── */}
      <Box sx={{ px: PAGE_PX, pb: '20px', flex: 1 }}>
        {visibleTypes.length === 0 && activeMaterialType === 'all' && (
          <ShortlistEmptyState
            message={`No materials shortlisted for ${activeRoom} yet.`}
          />
        )}

        {visibleTypes.map((mt) => {
          const sectionItems = items
            .filter((i) => i.cat_materials === mt.id)
            .sort((a, b) => a.ranking - b.ranking);
          return (
            <Box key={mt.id} component="section" sx={{ mb: '20px' }}>
              <ShortlistSectionHeader label={mt.label} count={sectionItems.length} />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(136px, 1fr))',
                gap: '8px',
              }}>
                {sectionItems.map((item, idx) => (
                  <MaterialCard
                    key={item.id} item={item} idx={idx} arrLen={sectionItems.length}
                    onMoveUp={() => move(mt.id, sectionItems, idx, -1)}
                    onMoveDown={() => move(mt.id, sectionItems, idx, 1)}
                    onRemove={() => removeItem(mt.id, sectionItems, item)}
                    onUpdated={handleItemUpdated}
                    onToggleSamples={onToggleSamples}
                    scenesData={scenesData}
                    onOpenSource={onOpenSource}
                    isGlowing={glowItemId === item.id}
                  />
                ))}
                <GhostCard onClick={() => setAddPhotoFor(mt.id)} aspectRatio="3/4" />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Hint strip ── */}
      <ShortlistHintStrip message={
        <><Box component="span" sx={{ fontWeight: 500, color: CHARCOAL }}>01 is your top pick.</Box>
        {' '}Use the ↑ ↓ controls to rank within each material type.
        Rank everything first, then mark what you want sent to you.</>
      } />

      {/* ── Add photo dialog ── */}
      <AddPhotoDialog
        open={!!addPhotoFor}
        categoryName={`${addPhotoFor} · ${activeRoom}`}
        onClose={() => setAddPhotoFor(null)}
        onUploaded={handlePhotoUploaded}
        bucketName="materrrials"
        filenameLabel={`${addPhotoFor}_${activeRoom}_${phoneNumber}`}
      />
    </>
  );
}

// ─── MaterialsShortlist (root) ────────────────────────────────────────────────
export default function MaterialsShortlist({ onRequestSamples }) {
  const toast = useToast();
  const phoneNumber = getTokenSubject();

  const [rooms,         setRooms]         = useState([]);   // { id, room_name }[]
  const [selectedRoom,  setSelectedRoom]  = useState('');   // room_name
  const [allMaterials,  setAllMaterials]  = useState([]);
  const [scenesData,    setScenesData]    = useState([]);
  const [status,        setStatus]        = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,     setLoadError]     = useState('');

  // Only the materials (used after uploads and other changes)
  const fetchMaterials = useCallback(async () => {
    try {
      setAllMaterials((await apiFetch('/api/MaterialsShortlist/my')) || []);
    } catch (err) {
      toast.error("We couldn't refresh your materials. Please reload the page.");
    }
  }, [toast]);

  // Everything the page needs on first load
  const loadAll = useCallback(async () => {
    setStatus('loading');
    try {
      const [roomsData, materialsData] = await Promise.all([
        apiFetch('/api/rooms/my'),
        apiFetch('/api/MaterialsShortlist/my'),
      ]);
      setRooms(roomsData || []);
      setSelectedRoom((prev) => prev || roomsData?.[0]?.room_name || '');
      setAllMaterials(materialsData || []);
      setStatus('ready');
    } catch (err) {
      setLoadError(friendlyMessage(err));
      setStatus('error');
    }
    // Photos are only used for the "came from this photo" link, so a failure
    // here isn't worth an error message: the link simply doesn't show.
    apiFetch('/api/scenes/my').then((d) => setScenesData(d || [])).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // "See in photo" pop-up. After it closes with changes, reload the list;
  // the room tab, filter chip and scroll position stay as they were.
  const sourcePeek = useSourcePeek(fetchMaterials);

  // Add a new room straight away
  const handleAddRoom = async (roomName) => {
    try {
      const newRoom = await apiFetch('/api/rooms', { method: 'POST', body: { room_name: roomName } });
      // The server returns the existing room if the name is already used.
      setRooms((prev) => (prev.some((r) => r.id === newRoom.id) ? prev : [...prev, newRoom]));
      setSelectedRoom(newRoom.room_name);
    } catch (err) {
      toast.error(`We couldn't add "${roomName}". Please try again.`);
    }
  };

  // Removing a room (the × on its tab). Its materials leave this page too.
  const roomRemoval = useRoomRemoval({
    rooms, setRooms, selectedRoom, setSelectedRoom,
    onRoomRemoved: (room) => setAllMaterials((prev) => prev.filter((m) => m.cat_room !== room.room_name)),
  });

  const activeRoom = rooms.some((r) => r.room_name === selectedRoom)
    ? selectedRoom
    : (rooms[0]?.room_name ?? '');
  const roomItems  = allMaterials.filter((m) => m.cat_room === activeRoom);

  const handleItemsChanged = (updatedRoomItems) => {
    setAllMaterials((prev) => {
      const rest = prev.filter((m) => m.cat_room !== activeRoom);
      return [...rest, ...updatedRoomItems];
    });
  };

  const handleRemoveItem = (id) => {
    setAllMaterials((prev) => prev.filter((m) => m.id !== id));
  };

  // Show the change straight away; undo it if the server says no.
  const handleToggleSamples = async (id, newValue) => {
    setAllMaterials((prev) =>
      prev.map((m) => (m.id === id ? { ...m, in_samples_request: newValue } : m))
    );
    try {
      await apiFetch(`/api/MaterialsShortlist/${id}/toggle-samples`, { method: 'PUT' });
    } catch (err) {
      setAllMaterials((prev) =>
        prev.map((m) => (m.id === id ? { ...m, in_samples_request: !newValue } : m))
      );
      toast.error(newValue
        ? "We couldn't add this to your samples request. Please try again."
        : "We couldn't remove this from your samples request. Please try again.");
    }
  };

  // ── Sample box limits ────────────────────────────────────────────────────
  const samplesMarked = allMaterials.filter((m) => m.in_samples_request);
  const countByType   = MATERIAL_TYPES.reduce((acc, mt) => {
    acc[mt.id] = samplesMarked.filter((m) => m.cat_materials === mt.id).length;
    return acc;
  }, {});
  const overQuotas  = MATERIAL_TYPES.filter((mt) => countByType[mt.id] > (QUOTAS[mt.id] ?? Infinity));
  const isOverQuota = overQuotas.length > 0;

  if (status === 'loading') {
    return <LoadingState label="Loading your materials…" size="page" />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load your materials"
        message={loadError}
        onRetry={loadAll}
      />
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', ...appGridBg,
    }}>

      {/* ── Page header ── */}
      <PageHeader
        title="Rank your"
        accent="materials."
        description="Materials you save from your moodboard appear here. Rank them, then choose which samples you'd like sent to your home."
      >
        <RoomTabStrip
          rooms={rooms}
          selectedRoom={activeRoom}
          onSelectRoom={setSelectedRoom}
          onAddRoom={handleAddRoom}
          onRemoveRoom={roomRemoval.requestRemove}
          checkingId={roomRemoval.checkingId}
        />
      </PageHeader>

      {/* ── Scrollable content — key forces a fresh start when the room changes ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <RemovalNotice
          items={sourcePeek.notice}
          onDismiss={sourcePeek.dismissNotice}
          sx={{ mx: PAGE_PX, mt: '14px' }}
        />
        <RoomTabContent
          key={activeRoom}
          activeRoom={activeRoom}
          items={roomItems}
          phoneNumber={phoneNumber}
          scenesData={scenesData}
          onItemsChanged={handleItemsChanged}
          onRemoveItem={handleRemoveItem}
          onToggleSamples={handleToggleSamples}
          onRefresh={fetchMaterials}
          onOpenSource={sourcePeek.openPeek}
          glowItemId={sourcePeek.glowItemId}
        />
      </Box>

      {roomRemoval.dialog}

      <SourcePhotoPeek
        peek={sourcePeek.peek}
        shortlistName="Materials"
        returnLabel="Materials"
        onClose={sourcePeek.closePeek}
        onClosedWithChanges={sourcePeek.handleClosedWithChanges}
      />

      {/* ── Sticky footer — sample counts + Request samples button ── */}
      <Box sx={{
        borderTop: `0.5px solid ${BORDER}`, background: '#fff',
        px: PAGE_PX, py: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        flexShrink: 0,
      }}>
        <Box aria-live="polite">
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
            {samplesMarked.length} marked across all rooms and types
          </Typography>
          {isOverQuota ? (
            overQuotas.map((mt) => (
              <Typography key={mt.id} sx={{ fontSize: '10px', color: WARNING, mt: '3px', ...bodyFont }}>
                {mt.label}: {countByType[mt.id]} marked, box fits {QUOTAS[mt.id]}.{' '}
                Remove {countByType[mt.id] - QUOTAS[mt.id]} to continue.
              </Typography>
            ))
          ) : (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '3px' }}>
              {MATERIAL_TYPES.map((mt) => `${mt.label} ${countByType[mt.id]}`).join(' · ')}
            </Typography>
          )}
        </Box>

        <Box
          component="button"
          type="button"
          onClick={isOverQuota ? undefined : onRequestSamples}
          disabled={isOverQuota}
          sx={{
            ...labelMono, fontSize: '9px',
            px: '20px', py: '10px', flexShrink: 0,
            border: isOverQuota ? `1px solid ${GREEN}` : 'none',
            background: isOverQuota ? 'transparent' : GREEN,
            color: isOverQuota ? GREEN : '#fff',
            cursor: isOverQuota ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': !isOverQuota ? { background: GREEN_HOVER } : {},
          }}
        >
          {isOverQuota ? 'Request samples' : `Request samples (${samplesMarked.length}) →`}
        </Box>
      </Box>
    </Box>
  );
}
